import React, { useCallback, useEffect, useState } from "react";
import { AlertTriangle, Check, ShieldOff, Trash2, X } from "lucide-react";

import Topbar from "../components/Topbar";
import {
  ConfirmDialog,
  EmptyState,
  ErrorState,
  TableSkeleton,
  useToast,
} from "../components/ui/Feedback";
import { FilterTabs, Pagination } from "../components/ui/Table";
import { useQuery } from "../lib/useQuery";
import {
  deleteComment,
  deletePost,
  deleteReply,
  fetchReportedContent,
  fetchReports,
  resolveReport,
  setSuspension,
  type ReportRow,
  type ReportStatus,
} from "../lib/api";
import { friendlyError } from "../lib/errorMessage";

/**
 * The moderation queue.
 *
 * App Store guideline 1.2 requires an app with user-generated content to act
 * on reports, not merely collect them — Apple's wording is that objectionable
 * content must be ejected and the offending user removed. A reports table
 * nobody works is the same as having no reporting at all, so this is the
 * other half of the feature.
 *
 * Every row carries the reported content itself, fetched on expand. Deciding
 * from a reason code alone ("harassment") is guesswork; deciding from the
 * actual text takes a couple of seconds and is usually obvious.
 *
 * Deletion goes through admin_delete_post / admin_delete_comment, which record
 * the author, an excerpt and a reason in the audit log before the row goes.
 */

const PAGE_SIZE = 20;

const REASON_LABELS: Record<string, string> = {
  spam: "Spam or scam",
  harassment: "Harassment",
  hate: "Hate speech",
  nudity: "Nudity / sexual",
  violence: "Violence or threats",
  self_harm: "Self-harm",
  misinformation: "Misinformation",
  other: "Other",
};

/** Reasons where the right default is removal, not a judgement call. */
const SEVERE = new Set(["hate", "violence", "self_harm", "nudity"]);

const timeAgo = (iso: string) => {
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
};

export default function Reports() {
  const toast = useToast();
  const [status, setStatus] = useState<ReportStatus | "all">("open");
  const [offset, setOffset] = useState(0);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [content, setContent] = useState<Record<string, { content: string | null; author: string | null } | null>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<ReportRow | null>(null);
  const [suspending, setSuspending] = useState<ReportRow | null>(null);

  const list = useQuery(
    () => fetchReports({ status, offset, limit: PAGE_SIZE }),
    [status, offset]
  );

  const rows = list.data?.rows ?? [];
  const total = list.data?.total ?? 0;

  // Reset paging whenever the filter changes, or page 3 of "open" becomes an
  // empty page 3 of "actioned".
  useEffect(() => {
    setOffset(0);
  }, [status]);

  const expand = useCallback(
    async (row: ReportRow) => {
      const next = expanded === row.id ? null : row.id;
      setExpanded(next);
      if (next && content[row.id] === undefined) {
        const found = await fetchReportedContent(row.target_type, row.target_id);
        setContent((prev) => ({ ...prev, [row.id]: found }));
      }
    },
    [expanded, content]
  );

  const resolve = useCallback(
    async (row: ReportRow, next: "actioned" | "dismissed", notes?: string) => {
      setBusy(row.id);
      try {
        await resolveReport(row.id, next, notes);
        toast.success(next === "actioned" ? "Report actioned" : "Report dismissed");
        list.refetch();
      } catch (err) {
        toast.failure(friendlyError(err, "Couldn't update that report."));
      } finally {
        setBusy(null);
      }
    },
    [toast, list]
  );

  const confirmDelete = useCallback(async () => {
    if (!deleting) return;
    setBusy(deleting.id);
    try {
      const reason = `Report: ${REASON_LABELS[deleting.reason] ?? deleting.reason}`;
      // Every branch must delete something. Falling through would close the
      // report as "Content removed" with the content still up.
      if (deleting.target_type === "post") {
        await deletePost(deleting.target_id, reason);
      } else if (deleting.target_type === "comment") {
        await deleteComment(deleting.target_id, reason);
      } else if (deleting.target_type === "reply") {
        await deleteReply(deleting.target_id, reason);
      } else {
        throw new Error("An account can't be removed — suspend it instead.");
      }
      await resolveReport(deleting.id, "actioned", "Content removed");
      toast.success("Content removed and report closed");
      setDeleting(null);
      list.refetch();
    } catch (err) {
      toast.failure(friendlyError(err, "Couldn't remove that content."));
    } finally {
      setBusy(null);
    }
  }, [deleting, toast, list]);

  const confirmSuspend = useCallback(async () => {
    if (!suspending?.target_user_id) return;
    setBusy(suspending.id);
    try {
      await setSuspension(
        suspending.target_user_id,
        true,
        `Report: ${REASON_LABELS[suspending.reason] ?? suspending.reason}`
      );
      await resolveReport(suspending.id, "actioned", "Account suspended");
      toast.success("Account suspended and report closed");
      setSuspending(null);
      list.refetch();
    } catch (err) {
      toast.failure(friendlyError(err, "Couldn't suspend that account."));
    } finally {
      setBusy(null);
    }
  }, [suspending, toast, list]);

  const openCount = status === "open" ? total : undefined;

  return (
    <>
      <Topbar
        title="Reports"
        subtitle={
          openCount !== undefined && openCount > 0
            ? `${openCount} waiting for review`
            : "Reported posts, comments and accounts"
        }
      />

      <div style={{ padding: 24 }}>
        <FilterTabs
          active={status}
          onChange={setStatus}
          tabs={[
            { key: "open" as const, label: "Open", count: openCount, tone: "danger" },
            { key: "actioned" as const, label: "Actioned" },
            { key: "dismissed" as const, label: "Dismissed" },
            { key: "all" as const, label: "All" },
          ]}
        />

        {list.loading ? (
          <TableSkeleton rows={6} />
        ) : list.error ? (
          <ErrorState error={list.error} onRetry={list.refetch} />
        ) : rows.length === 0 ? (
          <EmptyState
            message={status === "open" ? "Nothing to review" : "No reports here"}
            hint={
              status === "open"
                ? "Reports from the app land here. An empty queue is the goal."
                : "Try another filter."
            }
          />
        ) : (
          <div style={{ display: "grid", gap: 10, marginTop: 16 }}>
            {rows.map((row) => {
              const isOpen = expanded === row.id;
              const body = content[row.id];
              const severe = SEVERE.has(row.reason);

              return (
                <div
                  key={row.id}
                  style={{
                    border: "1px solid var(--border)",
                    borderLeft: `4px solid ${severe ? "var(--danger)" : "var(--border-strong)"}`,
                    borderRadius: "var(--radius)",
                    background: "#fff",
                    overflow: "hidden",
                  }}
                >
                  <button
                    onClick={() => expand(row)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      width: "100%",
                      padding: "13px 15px",
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      textAlign: "left",
                    }}
                  >
                    {severe && <AlertTriangle size={15} color="var(--danger)" />}
                    <span style={{ fontWeight: 800, fontSize: 13 }}>
                      {REASON_LABELS[row.reason] ?? row.reason}
                    </span>
                    <span style={{ fontSize: 12, color: "var(--text-muted)" }}>
                      {row.target_type}
                      {row.target_user?.username ? ` by @${row.target_user.username}` : ""}
                    </span>
                    <span style={{ marginLeft: "auto", fontSize: 11.5, color: "var(--text-muted)" }}>
                      {row.reporter?.username ? `from @${row.reporter.username} · ` : ""}
                      {timeAgo(row.created_at)}
                    </span>
                    {row.status !== "open" && (
                      <span
                        style={{
                          fontSize: 11,
                          fontWeight: 800,
                          textTransform: "uppercase",
                          color: row.status === "actioned" ? "var(--success)" : "var(--text-muted)",
                        }}
                      >
                        {row.status}
                      </span>
                    )}
                  </button>

                  {isOpen && (
                    <div style={{ padding: "0 15px 15px", borderTop: "1px solid var(--border)" }}>
                      {!!row.details && (
                        <p style={{ fontSize: 13, margin: "12px 0 0", color: "var(--text)" }}>
                          <strong>Reporter said:</strong> {row.details}
                        </p>
                      )}

                      <div
                        style={{
                          marginTop: 12,
                          padding: 12,
                          borderRadius: "var(--radius)",
                          background: "var(--surface-alt, #f7f7f9)",
                          fontSize: 13,
                          whiteSpace: "pre-wrap",
                        }}
                      >
                        {body === undefined ? (
                          "Loading the reported content…"
                        ) : body === null ? (
                          <em style={{ color: "var(--text-muted)" }}>
                            This content no longer exists — it was already deleted.
                          </em>
                        ) : (
                          body.content || <em style={{ color: "var(--text-muted)" }}>No text — media only. Check the Community page.</em>
                        )}
                      </div>

                      {row.status === "open" && (
                        <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
                          {row.target_type !== "user" && body !== null && (
                            <button
                              className="btn btn-sm"
                              onClick={() => setDeleting(row)}
                              disabled={busy === row.id}
                              style={{ color: "var(--danger)", borderColor: "#f6c9cf" }}
                            >
                              <Trash2 size={13} /> Remove content
                            </button>
                          )}
                          {!!row.target_user_id && (
                            <button
                              className="btn btn-sm"
                              onClick={() => setSuspending(row)}
                              disabled={busy === row.id}
                              style={{ color: "var(--danger)", borderColor: "#f6c9cf" }}
                            >
                              <ShieldOff size={13} /> Suspend account
                            </button>
                          )}
                          <button
                            className="btn btn-sm"
                            onClick={() => resolve(row, "actioned", "Reviewed, handled elsewhere")}
                            disabled={busy === row.id}
                          >
                            <Check size={13} /> Mark actioned
                          </button>
                          <button
                            className="btn btn-sm"
                            onClick={() => resolve(row, "dismissed", "No breach found")}
                            disabled={busy === row.id}
                          >
                            <X size={13} /> Dismiss
                          </button>
                        </div>
                      )}

                      {row.status !== "open" && !!row.admin_notes && (
                        <p style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 12 }}>
                          {row.admin_notes}
                          {row.reviewed_at ? ` · ${new Date(row.reviewed_at).toLocaleString("en-GB")}` : ""}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <Pagination offset={offset} limit={PAGE_SIZE} total={total} onChange={setOffset} />
      </div>

      <ConfirmDialog
        open={!!deleting}
        busy={busy === deleting?.id}
        destructive
        title="Remove this content?"
        body={
          <>
            The {deleting?.target_type} is deleted and the report closed. The author,
            an excerpt and the reason are recorded in the audit log first.
          </>
        }
        confirmLabel="Remove"
        onConfirm={confirmDelete}
        onCancel={() => setDeleting(null)}
      />

      <ConfirmDialog
        open={!!suspending}
        busy={busy === suspending?.id}
        destructive
        title="Suspend this account?"
        body={
          <>
            {suspending?.target_user?.username
              ? `@${suspending.target_user.username}`
              : "This account"}{" "}
            will be signed out and blocked from using the app until an admin lifts it.
            Their existing posts stay up — remove those separately if they breach the rules.
          </>
        }
        confirmLabel="Suspend"
        onConfirm={confirmSuspend}
        onCancel={() => setSuspending(null)}
      />
    </>
  );
}
